// Client-side deduplication and S2→PaperCreate transforms

import type { PaperCreate } from "@/types/paper";
import type { S2CitationEdge, S2Paper } from "./s2-client";
import { mergeLinks } from "@/lib/provenance";

export interface RawTrawledPaper extends PaperCreate {
  is_influential?: boolean;
  contexts?: string[];
  intents?: string[];
  fields_of_study?: string[];
  is_open_access?: boolean;
  open_access_pdf?: string;
  influentialCitationCount?: number;
}

/**
 * Deduplicate papers by paperId. When duplicates found, keep the entry
 * with the longer abstract (discovered_via links are unioned across all
 * duplicates). Papers without paper_id are always kept.
 */
export function deduplicatePapers(papers: RawTrawledPaper[]): RawTrawledPaper[] {
  const seen = new Map<string, RawTrawledPaper>();
  const noPaperId: RawTrawledPaper[] = [];

  for (const paper of papers) {
    if (!paper.paper_id) {
      noPaperId.push(paper);
      continue;
    }

    const existing = seen.get(paper.paper_id);
    if (!existing) {
      seen.set(paper.paper_id, paper);
    } else {
      // Keep the one with the longer abstract, but union discovery links
      // so collapsing duplicates never loses provenance.
      const links = mergeLinks(existing.discovered_via ?? [], paper.discovered_via ?? []);
      const existingLen = existing.abstract?.length ?? 0;
      const newLen = paper.abstract?.length ?? 0;
      const keep = newLen > existingLen ? paper : existing;
      seen.set(paper.paper_id, links.length ? { ...keep, discovered_via: links } : keep);
    }
  }

  return [...seen.values(), ...noPaperId];
}

/**
 * Extract the paper from a citation/reference edge and convert to PaperCreate.
 * For "citations" direction: the citing paper is the discovered one.
 * For "references" direction: the cited paper is the discovered one.
 */
export function edgeToPaperCreate(
  edge: S2CitationEdge,
  direction: "citations" | "references",
): RawTrawledPaper | null {
  const paper = direction === "citations" ? edge.citingPaper : edge.citedPaper;
  // No paperId = a reference S2 could not resolve; its "title" is raw parser
  // text ("Did you include the total amount of compute…", "born 6 November
  // 1980) is a Norwegian … actor") with no authors or DOI. Mirrors the
  // backend's s2_client.edge_to_paper_create, which always required it —
  // this side drifted and let 58 such rows into prod corpora (2026-09-28).
  if (!paper || !paper.paperId || !paper.title) return null;

  return {
    ...s2PaperToFields(paper, paper.paperId),
    is_influential: edge.isInfluential ?? false,
    contexts: edge.contexts ?? [],
    intents: edge.intents ?? [],
  };
}

/**
 * Convert a flat S2Paper to PaperCreate shape.
 */
export function s2PaperToPaperCreate(paper: S2Paper): RawTrawledPaper | null {
  if (!paper.paperId || !paper.title) return null;
  return s2PaperToFields(paper, paper.paperId);
}

function s2PaperToFields(paper: S2Paper, paperId: string): RawTrawledPaper {
  const doi = paper.externalIds?.DOI ?? undefined;
  const authors = paper.authors?.map((a) => a.name) ?? [];
  const journal =
    paper.journal?.name ??
    paper.publicationVenue?.name ??
    paper.venue ??
    "";

  return {
    title: paper.title,
    authors,
    year: paper.year ?? undefined,
    journal,
    doi,
    url: paper.url ?? "",
    paper_id: paperId,
    citations: paper.citationCount ?? 0,
    abstract: paper.abstract ?? undefined,
    is_influential: false,
    contexts: [],
    intents: [],
    fields_of_study: paper.fieldsOfStudy ?? [],
    is_open_access: paper.isOpenAccess ?? false,
    open_access_pdf: paper.openAccessPdf?.url ?? undefined,
    influentialCitationCount: paper.influentialCitationCount ?? 0,
  };
}
